import { Signal, effect, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { EMPTY, merge, of, switchMap } from 'rxjs';

/** Asks the browser for its native leave prompt, only while `dirty` is true. Call in an injection context. */
export function warnOnTabCloseWhile(dirty: Signal<boolean>): void {
  effect((onCleanup) => {
    if (!dirty()) return;
    const handler = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', handler);
    onCleanup(() => window.removeEventListener('beforeunload', handler));
  });
}

/**
 * Asks for the leave prompt while `hasUnsavedChanges()` is true. Control dirtiness is not a
 * signal, so it is re-read on every event of `control` and of whichever form `swappable` holds,
 * since pages replace that form when they load a text. Call in an injection context.
 */
export function warnOnTabCloseWhileUnsaved(
  control: AbstractControl,
  swappable: Signal<AbstractControl | null>,
  hasUnsavedChanges: () => boolean
): Signal<boolean> {
  const dirty = signal(false);
  merge(
    control.events,
    toObservable(swappable).pipe(
      switchMap((form) => (form ? merge(of(null), form.events) : EMPTY))
    )
  )
    .pipe(takeUntilDestroyed())
    .subscribe(() => dirty.set(hasUnsavedChanges()));
  warnOnTabCloseWhile(dirty);
  return dirty.asReadonly();
}
